"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.roundTo = roundTo;
exports.mean = mean;
exports.variance = variance;
exports.standardDeviation = standardDeviation;
exports.pValue = pValue;
exports.calculateStatisticalSignificance = calculateStatisticalSignificance;
exports.calculatePairedSignificance = calculatePairedSignificance;
exports.calculateWinRateCI = calculateWinRateCI;
exports.applyBenjaminiHochberg = applyBenjaminiHochberg;
exports.bootstrapMeanCI = bootstrapMeanCI;
const lodash_1 = require("lodash");
const stats_base_dists_normal_1 = require("@stdlib/stats-base-dists-normal");
const statistical_significance_rating_1 = require("../Types/statistical-significance-rating");
const Z_95 = 1.96;
function roundTo(value, numDigits) {
    return (0, lodash_1.round)(value, numDigits);
}
function mean({ values, numDigits = 3 }) {
    if (values.length === 0) {
        return 0;
    }
    const sum = values.reduce((acc, val) => acc + val, 0);
    const result = sum / values.length;
    return (0, lodash_1.round)(result, numDigits);
}
function variance({ values, numDigits = 3 }) {
    if (values.length < 2) {
        return 0;
    }
    const meanValue = mean({ values, numDigits: 10 });
    const squaredDifferences = values.map((val) => Math.pow(val - meanValue, 2));
    const sumSquaredDifferences = squaredDifferences.reduce((acc, val) => acc + val, 0);
    const result = sumSquaredDifferences / (values.length - 1);
    return (0, lodash_1.round)(result, numDigits);
}
function standardDeviation({ values, numDigits = 3 }) {
    if (values.length < 2) {
        return 0;
    }
    const varianceValue = variance({ values, numDigits: 10 });
    const result = Math.sqrt(varianceValue);
    return (0, lodash_1.round)(result, numDigits);
}
function pValue(aMean, aStdDev, bMean, bStdDev, twoSided = false) {
    const difference = aMean - bMean;
    const standardError = Math.sqrt(aStdDev * aStdDev + bStdDev * bStdDev);
    const zScore = difference / standardError;
    if (twoSided) {
        return 2 * (1 - (0, stats_base_dists_normal_1.cdf)(Math.abs(zScore), 0, 1));
    }
    else {
        return zScore > 0 ?
            1 - (0, stats_base_dists_normal_1.cdf)(zScore, 0, 1)
            : (0, stats_base_dists_normal_1.cdf)(zScore, 0, 1);
    }
}
function calculateStatisticalSignificance(value1, value2) {
    const _pValue = ((value1.standardDeviation !== undefined && value1.standardDeviation > 0) ||
        (value2.standardDeviation !== undefined && value2.standardDeviation > 0)) ?
        pValue(value1.value, value1.standardDeviation ?? 0, value2.value, value2.standardDeviation ?? 0)
        : null;
    const significance = _pValue === null ? null
        : _pValue <= 0.01 ? statistical_significance_rating_1.StatisticalSignificanceQualitativeRating.verySignificant
            : _pValue <= 0.05 ? statistical_significance_rating_1.StatisticalSignificanceQualitativeRating.significant
                : _pValue <= 0.157 ? statistical_significance_rating_1.StatisticalSignificanceQualitativeRating.suggestive
                    : statistical_significance_rating_1.StatisticalSignificanceQualitativeRating.indistinctFromNoise;
    return { pValue: _pValue, significance };
}
function calculatePairedSignificance(deltas) {
    const n = deltas.length;
    if (n < 2) {
        return { meanDelta: deltas[0] ?? 0, stdDev: 0, n, pValue: null, significance: null };
    }
    const meanDelta = mean({ values: deltas });
    const stdDev = standardDeviation({ values: deltas });
    const se = stdDev / Math.sqrt(n);
    const { pValue: p, significance } = calculateStatisticalSignificance({ value: meanDelta, standardDeviation: se }, { value: 0 });
    return { meanDelta, stdDev, n, pValue: p, significance };
}
function calculateWinRateCI(wins, losses, n) {
    if (n === 0) {
        return null;
    }
    const winRate = (0, lodash_1.round)(wins / n, 4);
    const lossRate = (0, lodash_1.round)(losses / n, 4);
    const tieRate = (0, lodash_1.round)(1 - winRate - lossRate, 4);
    const z2 = Z_95 * Z_95;
    const center = (wins + z2 / 2) / (n + z2);
    const margin = (Z_95 * Math.sqrt((wins * (n - wins)) / n + z2 / 4)) / (n + z2);
    return {
        winRate,
        lossRate,
        tieRate,
        ciLow: (0, lodash_1.round)(Math.max(0, center - margin), 4),
        ciHigh: (0, lodash_1.round)(Math.min(1, center + margin), 4),
        n,
    };
}
function applyBenjaminiHochberg(rawPValues) {
    const m = rawPValues.length;
    if (m === 0) {
        return [];
    }
    const indexed = rawPValues.map((p, i) => ({ p, i }));
    indexed.sort((a, b) => a.p - b.p);
    const adjusted = new Array(m);
    for (let rank = 0; rank < m; rank++) {
        adjusted[rank] = Math.min(1, (indexed[rank].p * m) / (rank + 1));
    }
    for (let rank = m - 2; rank >= 0; rank--) {
        adjusted[rank] = Math.min(adjusted[rank], adjusted[rank + 1]);
    }
    const result = new Array(m);
    for (let rank = 0; rank < m; rank++) {
        result[indexed[rank].i] = (0, lodash_1.round)(adjusted[rank], 6);
    }
    return result;
}
function bootstrapMeanCI(values, opts) {
    if (values.length < 2) {
        return null;
    }
    const resamples = opts?.resamples ?? 1000;
    const alpha = opts?.alpha ?? 0.05;
    const n = values.length;
    const means = [];
    for (let b = 0; b < resamples; b++) {
        let sum = 0;
        for (let j = 0; j < n; j++) {
            sum += values[Math.floor(Math.random() * n)];
        }
        means.push(sum / n);
    }
    means.sort((a, b) => a - b);
    const lo = Math.floor((alpha / 2) * resamples);
    const hi = Math.ceil((1 - alpha / 2) * resamples) - 1;
    return {
        mean: (0, lodash_1.round)(values.reduce((s, v) => s + v, 0) / n, 4),
        ciLow: (0, lodash_1.round)(means[lo], 4),
        ciHigh: (0, lodash_1.round)(means[hi], 4),
    };
}
//# sourceMappingURL=statistics-formulas.js.map